import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { api, apiError } from "@/lib/api";

const MAX_POLLS = 12;
const POLL_INTERVAL_MS = 2000;

const Card = ({ children, testid }) => (
  <div className="panel mx-auto max-w-lg p-6 text-center" data-testid={testid}>
    {children}
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("pending");
  const [message, setMessage] = useState("");
  const attempts = useRef(0);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState("error");
      setMessage("Geen betaalsessie gevonden.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("paid");
        return;
      }
      if (["expired", "failed"].includes(data.payment_status)) {
        setState("error");
        setMessage("De betaling is niet afgerond.");
        return;
      }
    } catch (e) {
      setState("error");
      setMessage(apiError(e));
      return;
    }
    attempts.current += 1;
    if (attempts.current >= MAX_POLLS) {
      setState("slow");
      return;
    }
    timer.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [sessionId]);

  useEffect(() => {
    poll();
    return () => clearTimeout(timer.current);
  }, [poll]);

  return (
    <AppShell title="Betaling" subtitle="Bevestiging van je ImDash-abonnement">
      {state === "pending" && (
        <Card testid="payment-pending">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <h2 className="mt-3 font-display text-lg font-semibold text-slate-900">
            Betaling wordt bevestigd…
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">Dit duurt meestal enkele seconden.</p>
        </Card>
      )}
      {state === "paid" && (
        <Card testid="payment-success">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
          <h2 className="mt-3 font-display text-lg font-semibold text-slate-900">
            Bedankt, je abonnement is actief
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">
            De factuur staat klaar op je abonnementspagina.
          </p>
          <Link to="/abonnement" className="btn-primary mt-4 inline-block">
            Naar abonnement
          </Link>
        </Card>
      )}
      {state === "slow" && (
        <Card testid="payment-slow">
          <Loader2 className="mx-auto h-8 w-8 text-slate-400" />
          <h2 className="mt-3 font-display text-lg font-semibold text-slate-900">
            Betaling nog in behandeling
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Sommige betaalmethoden hebben iets langer nodig. Je ziet de status op je
            abonnementspagina.
          </p>
          <Link to="/abonnement" className="btn-secondary mt-4 inline-block">
            Naar abonnement
          </Link>
        </Card>
      )}
      {state === "error" && (
        <Card testid="payment-error">
          <XCircle className="mx-auto h-8 w-8 text-red-600" />
          <h2 className="mt-3 font-display text-lg font-semibold text-slate-900">
            Betaling niet afgerond
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">{message}</p>
          <Link to="/abonnement" className="btn-secondary mt-4 inline-block">
            Opnieuw proberen
          </Link>
        </Card>
      )}
    </AppShell>
  );
};

export const PaymentCancelled = () => (
  <AppShell title="Betaling geannuleerd" subtitle="Er is niets afgeschreven">
    <Card testid="payment-cancelled">
      <XCircle className="mx-auto h-8 w-8 text-slate-400" />
      <h2 className="mt-3 font-display text-lg font-semibold text-slate-900">
        Betaling geannuleerd
      </h2>
      <p className="mt-1 text-[13px] text-slate-500">
        Je kunt het abonnement later activeren; er is geen bedrag afgeschreven.
      </p>
      <Link to="/abonnement" className="btn-primary mt-4 inline-block">
        Terug naar abonnement
      </Link>
    </Card>
  </AppShell>
);
